import { canEditRecord } from '../../lib/recordAccess';
import { useEffect, useState } from "react"
import OppsByStatus from "./OppsByStatus"
import { useParams } from "react-router-dom"
import { API } from "../../lib/api"
import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, Tab, Tabs, TextField } from "@mui/material"
import Feedback from "../../components/Feedback/Feedback"
import Contacts from "./Contacts"
import { AddCircle, ExpandLess, ExpandMore, InfoOutlined, ContactsOutlined, WorkOutline } from "@mui/icons-material"
import RecordModal from "../../components/RecordModal/RecordModal"
import { FormProvider } from "../../contexts/CreateRecordFormContext"
import { useAuth } from "../../contexts/AuthContext"
import SingleCustomerOppsColumns from "../../lib/TableColumns/SingleCustomerOppsColumns"
import CollapsibleGridCard from "../../components/CollapsibleGridCard"

const Content = () => {
    const [tab, setTab] = useState(0);
    const [visitedTabs, setVisitedTabs] = useState([0]);
    const changeTab = (_, next) => {
        setTab(next);
        setVisitedTabs(previous => previous.includes(next) ? previous : [...previous, next]);
    };
    const [customer, setCustomer] = useState({})
    const [opportunities, setOpportunities] = useState([])
    const [allOpportunities, setAllOpportunities] = useState([])
    const [state, setState] = useState(customer?.state || "")
    const [city, setCity] = useState(customer?.city || "")
    const [address, setAddress] = useState(customer?.address || "")
    const [status, setStatus] = useState(customer?.status || "")
    const [notes, setNotes] = useState(customer?.notes || "")
    const [feedback, setFeedback] = useState({open: false, message: "", type: ""})
    const [contacts, setContacts] = useState([])
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [formattedContacts, setFormattedContacts] = useState([])
    const [filterStatus, setFilterStatus] = useState("All")
    const [collapsedOverview, setCollapsedOverview] = useState(false)
    const [postalCode, setPostalCode] = useState(customer?.postalCode || "")

    const { user } = useAuth();
    const canEdit = canEditRecord(user, customer);

    const { cid } = useParams();

    const handleOpenCreateModal = () => setOpenCreateModal(true);
    const handleCloseCreateModal = () => setOpenCreateModal(false);

    const oppColumns = SingleCustomerOppsColumns()
    const filterStatusList = ['All', '01 - Lead', '02 - Identified', '06 - Quote Sent', '08 - Won']

    const oppFilterNode = (
        <div className="hive-single-customer-opps-filter">
            <FormControl fullWidth>
                <InputLabel id="single-customer-filter-status-label">Filter Status</InputLabel>
                <Select
                    labelId="single-customer-filter-status-label"
                    id="single-customer-filter-status"
                    value={filterStatus}
                    label="Filter Status"
                    onChange={(e) => handleFilterChange(e.target.value)}
                >
                    {filterStatusList.map(s => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </div>
    );

    const oppActionButtons = [
        { title: `Create New Opportunity for ${customer?.organizationName || "Customer"}`, onClick: handleOpenCreateModal, icon: AddCircle, color: "primary" },
    ];

    const handleFilterChange = (value) => {
        setFilterStatus(value);

        if (value === "All") {
            setOpportunities(allOpportunities);
        } else {
            const filtered = allOpportunities.filter(opp => opp.salesStage === value);
            setOpportunities(filtered);
        }
    };

    const handleSubmit = async () => {
        const tempData = {
            ...(address && { address }),
            ...(status && { status }),
            ...(notes && { notes }),
            ...(state && {state }),
            ...(city && {city }),
            ...(postalCode && { postalCode } )
        }

        const response = await API.Customers.UpdateOne(cid, tempData)
        if (response.status === 200) {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "success" }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "error" }))
        }
    }

    const handleCreateSubmit = async (data) => {
        let primaryContact = data?.primaryContact?.value

        const requestReadyData = {
            owner: localStorage.id,
            customer: cid,
            name: data.name,
            createdBy: user.email,
            lastUpdatedBy: user.email,
            lastUpdatedDate: Date.now(),
            salesStage: "01 - Lead",
            ...(primaryContact && { primaryContact }),
        }
        const response = await API.Opportunities.Create(requestReadyData)

        if (response.status === 200) {
            setOpportunities(prevOpportunities => [response.data.opportunity, ...prevOpportunities]);
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "success" }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "error" }))
        }
        handleCloseCreateModal()
    }

    const createFields = [
        { name: 'name', label: "Opportunity Name", type: "text", required: true },
        { name: 'primaryContact', label: "Primary Contact", type: "autocomplete", options: formattedContacts },
    ]

    useEffect(() => {
        let cancelled = false;
        async function getData() {
            const response = await API.Customers.FetchById(cid)
            const oppsResponse = await API.Opportunities.FetchByCustomer(cid)
            if (cancelled) return;

            setOpportunities(oppsResponse)
            setAllOpportunities(oppsResponse)

            setCustomer(response)

            setCity(response?.city)
            setState(response?.state)
            setAddress(response.address)
            setStatus(response.status)
            setNotes(response?.notes)
            setContacts(response?.contacts)
            setPostalCode(response?.postalCode)


            let tempContacts = response?.contacts.map(contact => {
                return { value: contact._id, label: contact.name}
            })
            setFormattedContacts(tempContacts)
        }
        getData()
        return () => { cancelled = true; };
    },[cid])

    return (
        <>
            <Feedback props={feedback} setFeedback={setFeedback} />
            <FormProvider>
        <div className="row hive-single-customer-page">
            <RecordModal open={openCreateModal} handleClose={handleCloseCreateModal} fields={createFields} title="Create New Opportunity" onSubmit={handleCreateSubmit} />
            <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tab} onChange={changeTab} aria-label="customer tabs" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
                    sx={{ '& .MuiTab-root': { flexShrink: 0, minWidth: { xs: 120, sm: 160 } } }}>
                    <Tab icon={<InfoOutlined />} label="General" id="customer-tab-0" aria-controls="customer-panel-0" />
                    <Tab icon={<ContactsOutlined />} label="Contacts" id="customer-tab-1" aria-controls="customer-panel-1" />
                    <Tab icon={<WorkOutline />} label="Opportunities" id="customer-tab-2" aria-controls="customer-panel-2" />
                </Tabs>
            </Box>
            <Box role="tabpanel" hidden={tab !== 0} id="customer-panel-0" aria-labelledby="customer-tab-0" sx={{ width: '100%' }}>
            {/* TEMP Customer Overview */}
            <div className="col-lg-12">
                <div className="card hive-single-customer-overview-card">
                    <div className="card-header hive-single-customer-overview-header">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>

                        <IconButton
                            className="hive-single-customer-overview-toggle"
                            onClick={() => setCollapsedOverview(!collapsedOverview)}
                            size="small"
                            sx={{ mr: 1 }}
                        >
                            {collapsedOverview ? <ExpandMore /> : <ExpandLess />}
                        </IconButton>
                        <h5 className="hive-single-customer-overview-title" style={{ margin: 0 }}>
                            Customer Overview - <span>{customer && customer.organizationName}</span>
                        </h5>
                    </Box>
                    </div>

                    <div style={{ display: collapsedOverview ? "none" : "block" }}>
                        <div className="card-body hive-single-customer-overview-body">

                            <h6 className="hive-single-customer-overview-section" style={{margin: "1em"}}>General</h6>

                            <div style={{display: "flex", justifyContent: "space-evenly", flexWrap: "wrap", marginBottom: "2em"}}>
                                <div className='col-10 col-lg-4' style={{margin: "0.5em"}}>
                                    <FormControl fullWidth>
                                        <TextField disabled={!canEdit}
                                            label="State"
                                            variant="outlined"
                                            value={state}
                                            onChange={(e) => setState(e.target.value)}
                                        />
                                    </FormControl>
                                </div>
                                <div className='col-10 col-lg-4' style={{margin: "0.5em"}}>
                                    <FormControl fullWidth>
                                        <TextField disabled={!canEdit}
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            label="City"
                                            variant="outlined"
                                        />
                                    </FormControl>
                                </div>
                                <div className='col-10 col-lg-4' style={{margin: "0.5em"}}>
                                    <FormControl fullWidth>
                                        <TextField disabled={!canEdit}
                                            value={address}
                                            onChange={(e) => setAddress(e.target.value)}
                                            label="Address"
                                            variant="outlined"
                                        />
                                    </FormControl>
                                </div>



                                <div className='col-10 col-lg-4' style={{margin: "0.5em"}}>
                                    <FormControl fullWidth>
                                        <TextField disabled={!canEdit}
                                            value={postalCode}
                                            onChange={(e) => setPostalCode(e.target.value)}
                                            label="Zip Code"
                                            variant="outlined"
                                        />
                                    </FormControl>
                                </div>

                                <div className='col-10 col-lg-4' style={{margin: "0.5em"}}>
                                <FormControl fullWidth>
                                    <InputLabel id="demo-simple-select-label">Status</InputLabel>
                                    <Select

                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        value={status} // would be the curr project.status
                                        label="Status"
                                        onChange={(e) => setStatus(e.target.value)}
                                    >
                                        <MenuItem value={"New"}>New</MenuItem>
                                        <MenuItem value={"Active"}>Active</MenuItem>
                                        <MenuItem value={"Inactive"}>Inactive</MenuItem>
                                        <MenuItem value={"Pending"}>Pending</MenuItem>
                                        <MenuItem value={"Dormant"}>Dormant</MenuItem>
                                        <MenuItem value={"Churned"}>Churned</MenuItem>
                                    </Select>
                                </FormControl>
                                </div>


                            </div>

                            <div style={{display: "flex", justifyContent: "space-evenly", flexWrap: "wrap", marginBottom: "2em"}}>
                                <div className='col-10 col-lg-8' style={{margin: "1em"}}>
                                    <FormControl fullWidth>
                                        <TextField disabled={!canEdit}
                                            multiline
                                            rows={10}
                                            label="Notes"
                                            variant="outlined"
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                        />
                                    </FormControl>
                                </div>
                            </div>

                            <div className='row' style={{display: "flex", justifyContent: "center", marginTop: "1em"}}>
                                <div className='col-lg-4'>
                                    <FormControl fullWidth>
                                        <Button className="hive-single-customer-overview-save" variant='outlined' disabled={!canEdit} onClick={handleSubmit}>Save</Button>
                                    </FormControl>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            </Box>
            <Box role="tabpanel" hidden={tab !== 1} id="customer-panel-1" aria-labelledby="customer-tab-1" sx={{ width: '100%' }}>
                {visitedTabs.includes(1) && <div className="row">
            <Contacts cid={cid} contacts={contacts} setContacts={setContacts} setFeedback={setFeedback} customer={customer} />
                </div>}
            </Box>
            <Box role="tabpanel" hidden={tab !== 2} id="customer-panel-2" aria-labelledby="customer-tab-2" sx={{ width: '100%' }}>
                {visitedTabs.includes(2) && <div className="row">
            <CollapsibleGridCard
                title={`Active Opportunities For - ${customer?.organizationName || ""}`}
                rows={opportunities}
                columns={oppColumns}
                getRowId={(row) => row._id}
                checkboxSelection
                filterNode={oppFilterNode}
                actionButtons={oppActionButtons}
                initialPageSize={10}
                pageSizeOptions={[10, 25, 50]}
                variantClassName="hive-single-customer-opps-card"
                headerClassName="hive-single-customer-opps-header"
                titleClassName="hive-single-customer-opps-title"
                toggleClassName="hive-single-customer-opps-toggle"
                contentClassName="hive-single-customer-opps-content"
                gridClassName="hive-single-customer-opps-grid"
                actionButtonsClassName="hive-single-customer-opps-actions"
            />


            <OppsByStatus opportunities={opportunities} organizationName={customer && customer.organizationName}/>
                </div>}
            </Box>

        </div>
        </FormProvider>
        </>
    )
}

export default Content
